import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { toast } from 'react-hot-toast'
import { vendorService } from '../../services/vendorService'
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Search,
  Filter,
  Star,
  MapPin,
  Calendar,
  Award,
  Briefcase,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  Mail,
  Phone,
  GraduationCap,
  Building2,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  FileText
} from 'lucide-react'
import DocumentViewerModal from './pages/DocumentViewerModal'

const VendorBenchResources = () => {
  const navigate = useNavigate()
  const [activeTab] = useState('all')
  const [benchResources, setBenchResources] = useState([])
  const [resourceSummary, setResourceSummary] = useState({
    total_resources: 0,
    available_resources: 0,
    on_project_resources: 0,
    avg_rating: 0
  })
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 8

  const [currentUser, setCurrentUser] = useState(null)
  const [isVendorActive, setIsVendorActive] = useState(false)
  const [profileLoading, setProfileLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [selectedResource, setSelectedResource] = useState(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [isDocModalOpen, setIsDocModalOpen] = useState(false)
  const [viewingDoc, setViewingDoc] = useState(null)

  const handleViewResource = async (resourceId) => {
    try {
      setViewLoading(true)
      setSelectedResource(null)
      const resp = await vendorService.getBenchResource(resourceId)
      
      // The API response has resource details nested inside data.resourse_details
      const responseData = resp?.data || resp
      const resourceDetails = responseData?.resourse_details || {}
      
      // Combine everything into a flat object for the UI to consume
      const flattenedResource = {
        ...resourceDetails,
        education: responseData?.education || [],
        experience: responseData?.experience || [],
        certifications: responseData?.certifications || [],
        documents: responseData?.documents || null,
        // Combine first and last name
        name: `${resourceDetails.first_name || ''} ${resourceDetails.last_name || ''}`.trim() || 'N/A'
      }

      setSelectedResource(flattenedResource)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Error fetching resource details:', error)
      toast.error('Failed to load resource details')
    } finally {
      setViewLoading(false)
    }
  }

  const handleViewDocument = (docUrl, docName, docType = 'document') => {
    if (!docUrl) {
      toast.error('Document URL not available')
      return
    }
    setViewingDoc({
      url: docUrl,
      name: docName,
      type: docType,
      fileName: docName
    })
    setIsDocModalOpen(true)
  }

  const handleEditResource = (resourceId) => {
    navigate(`/vendor/dashboard/add-bench-resource?id=${resourceId}`)
  }

  const handleDeleteResource = async (resourceId) => {
    try {
      setLoading(true)
      await vendorService.deleteBenchResource(resourceId)
      toast.success('Resource deleted successfully')
      fetchBenchResources()
    } catch (error) {
      console.error('Error deleting resource:', error)
      toast.error(error.message || 'Failed to delete resource')
    } finally {
      setLoading(false)
    }
  }

  const loadCurrentUser = async () => {
    setProfileLoading(true)
    try {
      const user = await vendorService.getCurrentUser()
      if (user) {
        setCurrentUser(user)
        const vendorId = user.vendor_id || user.vendor?.vendor_id || null
        if (vendorId) {
          sessionStorage.setItem('vendorId', JSON.stringify(vendorId))
        }
        const active = user.vendor_status === 'active' || user.status === 'active' || (user.vendor && user.vendor.status === 'active')
        setIsVendorActive(!!active)
      }
    } catch (error) {
      console.error('Error fetching current user:', error)
    } finally {
      setProfileLoading(false)
    }
  }

  useEffect(() => {
    loadCurrentUser()
  }, [])

  useEffect(() => {
    if (!profileLoading) {
      fetchBenchResources()
    }
  }, [profileLoading, currentUser])

  const fetchBenchResources = async () => {
    try {
      setLoading(true)
      let vendorId = sessionStorage.getItem('vendorId')
      if (vendorId) {
        try { vendorId = JSON.parse(vendorId) } catch (e) { /* keep as string */ }
      }
      if (!vendorId && currentUser?.vendor_id) {
        vendorId = currentUser.vendor_id
      }

      if (!vendorId) {
        toast.error('Vendor ID not found. Please login again.')
        return
      }

      const response = await vendorService.getVendorBenchResources(vendorId)
      let resources = response.data?.resources || response.data?.data?.resources || []
      const summary = response.data?.summary || response.data?.data?.summary || {
        total_resources: 0,
        available_resources: 0,
        on_project_resources: 0,
        avg_rating: 0
      }

      // Ensure each resource has a combined name field if it doesn't already
      if (Array.isArray(resources)) {
        resources = resources.map(res => ({
          ...res,
          name: res.name || `${res.first_name || ''} ${res.last_name || ''}`.trim() || 'N/A'
        }))
      }
      
      setBenchResources(Array.isArray(resources) ? resources : [])
      setResourceSummary(summary)
    } catch (error) {
      console.error('Error fetching bench resources:', error)
      toast.error('Failed to load bench resources')
    } finally {
      setLoading(false)
    }
  }

  const filteredResources = benchResources || []
  const totalPages = Math.ceil(filteredResources.length / itemsPerPage)
  const currentItems = filteredResources.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const getPageNumbers = () => {
    const pages = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (currentPage > 3) pages.push('...')
      
      let start = Math.max(2, currentPage - 1)
      let end = Math.min(totalPages - 1, currentPage + 1)
      
      if (currentPage <= 3) end = 4
      if (currentPage >= totalPages - 2) start = totalPages - 3
      
      for (let i = start; i <= end; i++) pages.push(i)
      
      if (currentPage < totalPages - 2) pages.push('...')
      pages.push(totalPages)
    }
    return pages
  }

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1)
  }, [benchResources.length])

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase()
    if (statusLower === 'available') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300">
          <CheckCircle className="w-4 h-4" />
          Available
        </span>
      )
    } else if (statusLower === 'on project' || statusLower === 'on-project') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
          <Clock className="w-4 h-4" />
          On Project
        </span>
      )
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300">
          <XCircle className="w-4 h-4" />
          Unavailable
        </span>
      )
    }
  }

  // Resource detail view
  if (viewLoading || selectedResource) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          {viewLoading ? (
            <div className="card p-12 text-center shadow-glow">
              <Loader className="w-12 h-12 mx-auto mb-4 animate-spin text-primary-600 dark:text-primary-400" />
              <p className="text-secondary-600 dark:text-secondary-400">Loading resource details...</p>
            </div>
          ) : selectedResource ? (
            <div className="space-y-4">
              {/* Header Card */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-4 shadow-glow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-600 via-primary-500 to-accent-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-lg font-bold">
                        {selectedResource.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                        {selectedResource.name || 'N/A'}
                      </h1>
                      <p className="text-sm text-secondary-600 dark:text-secondary-400">{selectedResource.designation || 'N/A'}</p>
                      {selectedResource.department && (
                        <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-0.5">{selectedResource.department}</p>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedResource(null)} 
                    className="btn-outline text-sm py-1.5 px-3 flex items-center gap-1.5"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Back
                  </button>
                </div>
              </motion.div>

              {/* Overview & Profile - Combined */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card p-6 shadow-glow"
              >
                <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-6">Overview & Profile</h3>
                
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-6">
                  <div>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Availability</p>
                    {getStatusBadge(selectedResource.availability)}
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Cost/Hour</p>
                    <p className="text-base font-semibold text-secondary-900 dark:text-secondary-100">
                      ${selectedResource.cost_per_hour ?? selectedResource.costPerHour ?? selectedResource.costperHour ?? '0'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Experience</p>
                    <p className="text-base font-semibold text-secondary-900 dark:text-secondary-100">
                      {selectedResource.totalExperience || selectedResource.total_experience || 'N/A'} Years
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Rating</p>
                    <p className="text-base font-semibold text-secondary-900 dark:text-secondary-100">
                      {selectedResource.rating ? `${selectedResource.rating}/5` : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
                  <h4 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-4">Contact Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Email</p>
                      <p className="text-sm text-secondary-900 dark:text-secondary-100">{selectedResource.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Phone</p>
                      <p className="text-sm text-secondary-900 dark:text-secondary-100">{selectedResource.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Location</p>
                      <p className="text-sm text-secondary-900 dark:text-secondary-100">{selectedResource.location || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Notice Period</p>
                      <p className="text-sm text-secondary-900 dark:text-secondary-100">{selectedResource.notice_period || selectedResource.noticePeriod || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Skills & Technologies */}
                <div>
                  <h4 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-4">Skills & Technologies</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs font-semibold text-secondary-600 dark:text-secondary-400 mb-2">Primary Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {(selectedResource.primary_skills || selectedResource.primarySkills || []).length > 0 ? (
                          (selectedResource.primary_skills || selectedResource.primarySkills || []).map((s, i) => (
                            <span key={i} className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs">
                              {s}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-secondary-500">None listed</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-secondary-600 dark:text-secondary-400 mb-2">Secondary Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {(selectedResource.secondary_skills || selectedResource.secondarySkills || []).length > 0 ? (
                          (selectedResource.secondary_skills || selectedResource.secondarySkills || []).map((s, i) => (
                            <span key={i} className="px-3 py-1 bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded-full text-xs">
                              {s}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-secondary-500">None listed</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-secondary-600 dark:text-secondary-400 mb-2">Technologies</p>
                      <div className="flex flex-wrap gap-2">
                        {(selectedResource.technologies || []).length > 0 ? (
                          (selectedResource.technologies || []).map((s, i) => (
                            <span key={i} className="px-3 py-1 bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 rounded-full text-xs">
                              {s}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-secondary-500">None listed</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Education & Experience - Combined */}
              {((selectedResource.education || []).length > 0 || (selectedResource.experience || []).length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="card p-6 shadow-glow"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Education */}
                    {(selectedResource.education || []).length > 0 && (
                      <div>
                        <h3 className="text-base font-bold text-secondary-900 dark:text-secondary-100 mb-4">
                          Education ({selectedResource.education?.length || 0})
                        </h3>
                        <div className="space-y-3">
                          {selectedResource.education.map((edu, i) => (
                            <div key={i} className="p-3 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg border border-secondary-200 dark:border-secondary-700">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{edu.degree || 'N/A'}</p>
                                  <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">{edu.institution || 'N/A'}</p>
                                  {edu.fieldOfStudy && (
                                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">Field: {edu.fieldOfStudy}</p>
                                  )}
                                </div>
                                <div className="text-right ml-3">
                                  {edu.yearOfPassing && (
                                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-xs">
                                      {edu.yearOfPassing}
                                    </span>
                                  )}
                                  {edu.percentage && (
                                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">{edu.percentage}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Experience */}
                    {(selectedResource.experience || []).length > 0 && (
                      <div>
                        <h3 className="text-base font-bold text-secondary-900 dark:text-secondary-100 mb-4">
                          Professional Experience ({selectedResource.experience?.length || 0})
                        </h3>
                        <div className="space-y-3">
                          {selectedResource.experience.map((exp, i) => (
                            <div key={i} className="p-3 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg border border-secondary-200 dark:border-secondary-700">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{exp.designation || 'N/A'}</p>
                                  <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">{exp.company || 'N/A'}</p>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ml-3 ${
                                  exp.isCurrent 
                                    ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                                    : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300'
                                }`}>
                                  {exp.isCurrent ? 'Current' : 'Past'}
                                </span>
                              </div>
                              <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">
                                {exp.startDate ? new Date(exp.startDate).toLocaleDateString() : 'N/A'} - {exp.isCurrent ? 'Present' : (exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'N/A')}
                              </p>
                              {exp.description && (
                                <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-2 line-clamp-2">{exp.description}</p>
                              )}
                              {exp.technologies && exp.technologies.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {exp.technologies.map((tech, techIdx) => (
                                    <span key={techIdx} className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-xs">
                                      {tech}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Experience Documents */}
                              {(exp.offerLetter?.url || exp.relievingLetter?.url || (exp.payslips && Object.values(exp.payslips).some(p => p.url))) && (
                                <div className="mt-4 pt-3 border-t border-secondary-100 dark:border-secondary-700">
                                  <p className="text-[10px] font-bold text-secondary-400 uppercase tracking-wider mb-2">Documents</p>
                                  <div className="flex flex-wrap gap-2">
                                    {exp.offerLetter?.url && (
                                      <button 
                                        onClick={() => handleViewDocument(exp.offerLetter.url, 'Offer Letter', 'offer_letter')}
                                        className="flex items-center gap-1.5 px-2 py-1 bg-secondary-100 dark:bg-secondary-700 hover:bg-primary-100 dark:hover:bg-primary-900/30 text-secondary-700 dark:text-secondary-300 hover:text-primary-700 dark:hover:text-primary-300 rounded text-[10px] font-semibold transition-colors"
                                      >
                                        <FileText className="w-3 h-3" />
                                        Offer Letter
                                      </button>
                                    )}
                                    {exp.relievingLetter?.url && (
                                      <button 
                                        onClick={() => handleViewDocument(exp.relievingLetter.url, 'Relieving Letter', 'relieving_letter')}
                                        className="flex items-center gap-1.5 px-2 py-1 bg-secondary-100 dark:bg-secondary-700 hover:bg-primary-100 dark:hover:bg-primary-900/30 text-secondary-700 dark:text-secondary-300 hover:text-primary-700 dark:hover:text-primary-300 rounded text-[10px] font-semibold transition-colors"
                                      >
                                        <FileText className="w-3 h-3" />
                                        Relieving Letter
                                      </button>
                                    )}
                                    {exp.payslips && Object.entries(exp.payslips).map(([key, payslip]) => (
                                      payslip.url && (
                                        <button 
                                          key={key}
                                          onClick={() => handleViewDocument(payslip.url, `Payslip ${key.replace('payslip', '')}`, 'payslip')}
                                          className="flex items-center gap-1.5 px-2 py-1 bg-secondary-100 dark:bg-secondary-700 hover:bg-primary-100 dark:hover:bg-primary-900/30 text-secondary-700 dark:text-secondary-300 hover:text-primary-700 dark:hover:text-primary-300 rounded text-[10px] font-semibold transition-colors"
                                        >
                                          <FileText className="w-3 h-3" />
                                          Payslip {key.replace('payslip', '')}
                                        </button>
                                      )
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Certifications & Languages - Combined */}
              {((selectedResource.certifications || []).length > 0 || (selectedResource.languages || []).length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="card p-6 shadow-glow"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Certifications */}
                    {(selectedResource.certifications || []).length > 0 && (
                      <div>
                        <h3 className="text-base font-bold text-secondary-900 dark:text-secondary-100 mb-4">
                          Certifications ({selectedResource.certifications?.length || 0})
                        </h3>
                        <div className="space-y-2">
                          {selectedResource.certifications.map((cert, i) => (
                            <div key={i} className="p-3 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg border border-secondary-200 dark:border-secondary-700">
                              <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{cert.certificationName || cert.name || 'N/A'}</p>
                              {cert.issuingOrganization && (
                                <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">Issued by: {cert.issuingOrganization}</p>
                              )}
                              <div className="flex flex-wrap gap-2 mt-2">
                                {cert.issueDate && (
                                  <span className="text-xs text-secondary-500 dark:text-secondary-400">
                                    Issued: {new Date(cert.issueDate).toLocaleDateString()}
                                  </span>
                                )}
                                {cert.expiryDate && (
                                  <span className="text-xs text-secondary-500 dark:text-secondary-400">
                                    Expires: {new Date(cert.expiryDate).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              {cert.credentialId && (
                                <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">ID: {cert.credentialId}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Languages */}
                    {(selectedResource.languages || []).length > 0 && (
                      <div>
                        <h3 className="text-base font-bold text-secondary-900 dark:text-secondary-100 mb-4">
                          Languages ({selectedResource.languages?.length || 0})
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedResource.languages.map((lang, i) => {
                            const langName = typeof lang === 'string' ? lang : (lang.languageName || lang.name || lang.language || 'Unknown')
                            const proficiency = typeof lang === 'string' ? '' : (lang.proficiencyLevel || lang.proficiency || lang.level || '')
                            return (
                              <span key={i} className="px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm">
                                {langName}{proficiency ? ` (${proficiency})` : ''}
                              </span>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          ) : null}
        </div>

        {/* Document Viewer Modal */}
        <DocumentViewerModal
          isOpen={isDocModalOpen}
          onClose={() => {
            setIsDocModalOpen(false)
            setViewingDoc(null)
          }}
          document={viewingDoc}
        />
      </div>
    )
  }

  // If vendor is not active
  if (!profileLoading && !isVendorActive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900 flex items-center justify-center p-6">
        <div className="card p-8 max-w-xl text-center shadow-glow">
          <Users className="w-16 h-16 mx-auto text-primary-600 dark:text-primary-400 mb-4" />
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100 mb-2">Vendor account not active</h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-6">Your vendor account needs to be active to add resources or perform bulk uploads. Please request activation or contact support.</p>
          <div className="flex justify-center gap-3">
            <button
              onClick={async () => { await loadCurrentUser(); toast.success('Refreshed status') }}
              className="btn-outline"
            >
              Refresh Status
            </button>
            <button
              onClick={() => toast.success('Activation request sent')}
              className="btn-primary"
            >
              Request Activation
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-secondary-100">Bench Resources</h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">Manage your team members and their availability</p>
        </div>
        <div className="flex items-center gap-3">
          {profileLoading ? (
            <div className="p-2"><Loader className="w-5 h-5 animate-spin text-secondary-400" /></div>
          ) : isVendorActive ? (
            <button 
              className="btn-primary px-4 py-2 text-sm font-semibold flex items-center gap-2 whitespace-nowrap"
              onClick={() => navigate('/vendor/dashboard/add-bench-resource')}
            >
              <Plus className="w-4 h-4" />
              <span>Add Resource</span>
            </button>
          ) : (
            <div className="p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg text-sm text-warning-800 dark:text-warning-300">
              Your vendor account is not active. You cannot add resources.
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards - Grouped */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-4 md:p-6 shadow-glow"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <div>
            <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Total Resources</p>
            <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{resourceSummary.total_resources}</p>
          </div>
          <div>
            <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Available</p>
            <p className="text-2xl font-bold text-success-600 dark:text-success-400">{resourceSummary.available_resources}</p>
          </div>
          <div>
            <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">On Projects</p>
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{resourceSummary.on_project_resources}</p>
          </div>
          <div>
            <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Avg. Rating</p>
            <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{resourceSummary.avg_rating.toFixed(1)}</p>
          </div>
        </div>
      </motion.div>

      {/* Search and Filter */}
      <div className="card p-4 shadow-glow">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search resources..."
              className="input pl-9 pr-3 w-full min-w-0"
            />
          </div>
          <button className="btn-outline px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2 whitespace-nowrap flex-shrink-0 dark:border-primary-600 dark:text-primary-400 dark:hover:bg-primary-900/20">
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </button>
        </div>

        {/* Resources Table */}
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="min-w-full inline-block align-middle">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-secondary-50 dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wider min-w-[200px]">
                    Resource
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wider min-w-[120px]">
                    Designation
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wider min-w-[100px]">
                    Experience
                  </th>
                  {/* <th className="px-3 py-3 text-left text-xs font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wider min-w-[120px]">
                    Location
                  </th> */}
                  <th className="px-3 py-3 text-left text-xs font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wider min-w-[100px]">
                    Cost/Hour
                  </th>
                  {/* <th className="px-3 py-3 text-left text-xs font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wider min-w-[110px]">
                    Status
                  </th> */}
                  <th className="px-3 py-3 text-left text-xs font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wider min-w-[100px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-secondary-900 divide-y divide-secondary-200 dark:divide-secondary-700">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center">
                      <Loader className="w-6 h-6 mx-auto animate-spin text-primary-600 dark:text-primary-400" />
                      <p className="mt-3 text-secondary-500 dark:text-secondary-400">Loading resources...</p>
                    </td>
                  </tr>
                ) : filteredResources.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center">
                      <Users className="w-12 h-12 mx-auto text-secondary-300 dark:text-secondary-600 mb-3" />
                      <p className="text-secondary-600 dark:text-secondary-400 text-lg">No resources found</p>
                      <p className="text-secondary-500 dark:text-secondary-500 text-sm mt-1">Try adjusting your filters or add a new resource</p>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((resource) => (
                    <motion.tr
                      key={resource.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors"
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center min-w-0">
                          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold mr-2 text-xs flex-shrink-0">
                            {resource.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                              {resource.name || 'N/A'}
                            </div>
                            <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                              {resource.email || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-secondary-900 dark:text-secondary-100 truncate">{resource.designation || 'N/A'}</div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate">{resource.department || 'N/A'}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-secondary-900 dark:text-secondary-100 whitespace-nowrap">
                          {resource.totalExperience || resource.total_experience || resource.experienceCount || 'N/A'} Years
                        </div>
                      </td>
                      {/* <td className="px-3 py-3">
                        <div className="flex items-center text-sm text-secondary-900 dark:text-secondary-100 min-w-0">
                          <MapPin className="w-3.5 h-3.5 mr-1 text-secondary-400 flex-shrink-0" />
                          <span className="truncate">{resource.location || 'N/A'}</span>
                        </div>
                      </td> */}
                      <td className="px-3 py-3">
                        <div className="text-sm text-secondary-900 dark:text-secondary-100 font-medium whitespace-nowrap">
                          ${resource.cost_per_hour || resource.costPerHour || resource.costperHour || '0'}/hr
                        </div>
                      </td>
                      {/* <td className="px-3 py-3">
                        <div className="flex items-center">
                          {getStatusBadge(resource.availability)}
                        </div>
                      </td> */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          <button 
                            onClick={() => handleViewResource(resource.id)} 
                            className="p-1.5 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 flex-shrink-0"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleEditResource(resource.id)}
                            className="p-1.5 text-secondary-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors rounded hover:bg-accent-50 dark:hover:bg-accent-900/20 flex-shrink-0"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteResource(resource.id)}
                            className="p-1.5 text-secondary-400 hover:text-error-600 dark:hover:text-error-400 transition-colors rounded hover:bg-error-50 dark:hover:bg-error-900/20 flex-shrink-0"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Section */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Showing <span className="font-semibold text-secondary-900 dark:text-secondary-100">{Math.min(filteredResources.length, (currentPage - 1) * itemsPerPage + 1)}</span> to <span className="font-semibold text-secondary-900 dark:text-secondary-100">{Math.min(filteredResources.length, currentPage * itemsPerPage)}</span> of <span className="font-semibold text-secondary-900 dark:text-secondary-100">{filteredResources.length}</span> resources
            </div>
            
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <div className="flex items-center gap-1">
                {getPageNumbers().map((pageNum, index) => (
                  <button
                    key={index}
                    onClick={() => typeof pageNum === 'number' && setCurrentPage(pageNum)}
                    disabled={pageNum === '...'}
                    className={`min-w-[36px] h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white shadow-md shadow-primary-200 dark:shadow-none'
                        : pageNum === '...'
                        ? 'text-secondary-400 cursor-default'
                        : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-800 border border-transparent hover:border-secondary-200 dark:hover:border-secondary-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VendorBenchResources
